import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import Button from "./button";
import { LeftArrow } from "./icons";

export type WizardStep = {
  title: string;
  step: React.ComponentType<{
    onContinue?: (handler: () => Promise<boolean>) => void;
  }>;
};

export interface WizardProps {
  steps: Array<WizardStep>;
  initialStep?: number;
  onComplete?: () => Promise<void>;
  persistKey?: string;
  completeCheckComponent: React.ComponentType<{
    onComplete: () => Promise<void>;
  }>;
}

export default function Wizard({
  steps,
  initialStep = 0,
  onComplete,
  persistKey,
  completeCheckComponent: CompleteCheckComponent,
}: WizardProps) {
  const { t } = useTranslation("common");
  const [currentStep, setCurrentStep] = useState(() => {
    if (persistKey) {
      const persisted = localStorage.getItem(persistKey);
      if (persisted) return Number(persisted);
    }
    return initialStep;
  });
  const stepContinueHandlerRef = React.useRef<(() => Promise<boolean>) | null>(
    null,
  );
  // Register without triggering a re-render
  const registerContinue = React.useCallback(
    (handler: () => Promise<boolean>) => {
      stepContinueHandlerRef.current = handler;
    },
    [],
  );
  const isLastStep = currentStep === steps.length - 1;

  React.useEffect(() => {
    if (persistKey) {
      localStorage.setItem(persistKey, String(currentStep));
    }
  }, [currentStep, persistKey]);

  React.useEffect(() => {
    stepContinueHandlerRef.current = null;
  }, [currentStep]);

  const handleContinue = async () => {
    const handler = stepContinueHandlerRef.current;
    if (typeof handler === "function") {
      const success = await handler();
      if (!success) {
        return;
      }
    }

    if (!isLastStep) {
      setCurrentStep((prev) => prev + 1);
    } else if (onComplete) {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    if (!isLastStep) {
      setCurrentStep((prev) => prev + 1);
    } else {
      if (onComplete) onComplete();
    }
  };

  const StepComponent = steps[currentStep].step;

  return (
    <div className="w-full p-6 bg-white ">
      <div className="relative flex flex-row w-full items-center justify-center">
        <div className="w-[54px] absolute left-0">
          {currentStep > 0 && (
            <Button variant="ghost" size="xs" onClick={handleBack}>
              <LeftArrow />{" "}
            </Button>
          )}
        </div>
        <h2 className="body-small text-center ">
          {steps[currentStep].title} ({currentStep + 1}/{steps.length})
        </h2>
      </div>
      <div className="w-full h-1 rounded-full bg-garnet/25 mt-2 mb-6 overflow-hidden">
        <div
          className="h-full bg-garnet transition-all"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>
      <div className="mb-6">
        <StepComponent onContinue={registerContinue} />
      </div>
      {currentStep < steps.length - 1 ? (
        <div className="flex gap-4 flex-row w-full justify-between items-center pt-20">
          <Button
            variant="secondary"
            onClick={handleContinue}
            className="w-[320px]"
          >
            {t("wizard.continue")}
          </Button>
          <Button variant="link" onClick={handleSkip}>
            {t("wizard.skipForNow")}
          </Button>
        </div>
      ) : (
        <div className="flex pt-20 flex-col">
          <CompleteCheckComponent
            onComplete={async () => {
              const handler = stepContinueHandlerRef.current;
              if (handler) {
                const success = await handler();
                if (!success) return;
              }
              if (onComplete) await onComplete();
            }}
          />
        </div>
      )}
      <div className="mt-4 body-small">
        <strong>{t("wizard.noteLabel")}</strong> {t("wizard.completeAllSteps")}
      </div>
    </div>
  );
}
